// @flow

import _ from 'lodash';
import localforage from 'localforage';
import Alert from 'react-s-alert';

import Map from './Map.jsx';
import App from './App.jsx';

import {ms_icons} from './consts.js';

import {round2, takeFilename, gen_snum, isNumber, es_curvas_nivel, es_acuatico, es_infra, culti_regex} from './utils.js';

import type {T_Point, T_IconPair} from './types.js';



export type T_Shp = {
  shp_id      : number,
  num_verts   : number,
  num_parts   : number,
  starts      : Array<number>,
  rings       : Array<Array<T_Point>>,
  Centroids   : Array<T_Point>,
  HolesIndexes: Array<number>,
  area?       : number
};


export type T_DbfRow = {
  [string]: string | number
};



export function dbf_row_id(row: T_DbfRow): number {
  if (row.ID && typeof row.ID === 'number') {
    return row.ID;
  }
  else if (row.ID && typeof row.ID === 'string' && isNumber(row.ID)) {
    return parseInt(row.ID, 10);
  }

  return -1;
}



export type T_TypeCol = {
  type    : string,
  width   : number,
  decimals: number
}


export type T_ShpFile = {
  shpfile         : string,
  is_mav          : number,
  shptype         : string,
  num_shapes      : number,
  zone            : string,
  utm_bounds      : Array<number>,
  min_bound       : T_Point,
  max_bound       : T_Point,
  shapes          : Array<T_Shp>,
  dbfRecords      : Array<T_DbfRow>,
  dbf_column_types: {[string]: T_TypeCol},
  html_body       : string,
  total_has?      : number,
  convs?          : {[string]: string}
}



export function fix_dbf(shape: T_ShpFile, notify: bool): T_ShpFile {
  const dbf_ids = _.map(shape.dbfRecords, 'ID');

  if (dbf_ids.length !== _.uniq(dbf_ids).length) {
    if (notify) {
      Alert.error('Los ID del dbf están repetidos. Aplicaré una solución parche...');
    }

    let count = 1;

    for (const dbf of shape.dbfRecords) {
      dbf.ID = count;

      count++;
    }
  }

  return shape;
}



export type T_Opa = {
  min   : number,
  max   : number,
  minPto: number,
  maxPto: number
};


type T_SumAreas = {
  sum: number,
  ids: Array<number>
};


export const EMPTY_SUM = {
  sum: 0,
  ids: []
};



export default class Shp {
  static border_color     : string     = '#000000';
  static weight_selected  : number     = 2.5;
  static weight_unselected: number     = 1;
  static icon_pair        : T_IconPair = ms_icons[1];
  static opa              : T_Opa      = {min: 0.03, max: 0.07, minPto: 0.4, maxPto: 1};
  static count            : number     = 0;

  static polygonOptions = {
    clickable    : true,
    fillColor    : '#000000',
    fillOpacity  : Shp.opa.min,
    geodesic     : false,
    strokeColor  : Shp.border_color,
    strokeOpacity: 1,
    strokeWeight : Shp.weight_unselected,
    zIndex       : 1
  };

  static polygonOptions_CF = {
    clickable: true,
    fillOpacity: 10,
    geodesic: false,
    strokeColor: Shp.border_color,
    strokeOpacity: 1,
    strokeWeight: Shp.weight_unselected,
    zIndex: 1
  }

  static polygonOptionsAqua = {
    clickable    : false,
    fillColor    : 'blue',
    fillOpacity  : 1,
    geodesic     : false,
    strokeColor  : 'blue',
    strokeOpacity: 1,
    strokeWeight : 3,
    zIndex       : 2000
  };

  static polygonOptionsInfra = {
    clickable    : false,
    fillColor    : 'red',
    fillOpacity  : 1,
    geodesic     : false,
    strokeColor  : 'red',
    strokeOpacity: 1,
    strokeWeight : 3,
    zIndex       : 1000
  };

  static lineOptions = {
    clickable    : true,
    strokeColor  : Shp.border_color,
    strokeOpacity: 1,
    strokeWeight : Shp.weight_unselected,
    fillColor    : '#000000',
    fillOpacity  : 0
  };

  static lineOptionsAqua = {
    clickable    : true,
    strokeColor  : 'blue',
    strokeOpacity: 1,
    strokeWeight : 2,
    fillColor    : 'blue',
    fillOpacity  : 1
  };

  static clickedPol  = null;
  static clickedMark = null;

  obj      : T_ShpFile;
  overlays : Array<Object>;
  labels   : Array<Object>;
  bounds   : Object;
  old      : number;
  is_culti : boolean;
  map      : Map;
  sum_areas: T_SumAreas;
  frame    : ?Object;



  static hilight_dot(ov: Object): void {
    if (Shp.clickedMark) {
      Shp.clickedMark.setIcon(Shp.icon_pair.normal);
    }

    if (ov) {
      ov.setIcon(Shp.icon_pair.hilight);
      Shp.clickedMark = ov;
    }
  }



  static hilight_pol(ov: Object, is_mav: boolean, is_aqua: boolean, replace: boolean): void {
    if (replace && Shp.clickedPol) {
      Shp.clickedPol.setOptions({fillOpacity: is_mav || is_aqua ? 0.7 : Shp.opa.min, strokeWeight: Shp.weight_unselected});
    }

    if (Shp.clickedPol === ov) {     // NOTE: SI SE CLICKEA UNA SEGUNDA VEZ, ESTE CÓDIGO LO VA A DESELECCIONAR.
      Shp.clickedPol = null;
      return;
    }

    if (ov) {
      ov.setOptions({fillOpacity: is_mav ? 0.95 : Shp.opa.max, strokeWeight: Shp.weight_selected});
      Shp.clickedPol = ov;
    }
  }



  static turn_off_pol(ov: Object, is_mav: boolean): void {
    if (ov) {
      ov.setOptions({fillOpacity: is_mav ? 0.7 : Shp.opa.min, strokeWeight: Shp.weight_unselected, fillColor: '#000000'});
    }
  }



  static click_pol(app: App, ov: Object, mav: boolean, path: string, id: number, recs: T_DbfRow, map_num: number, add: boolean): void {
    let show_dbf = true;

    if (app.toolbar != null) {
      app.toolbar.setState({sel_pol: {path, id}});
    }

    const shp  = app.map_ui.map.Shp_List[path],
          file = takeFilename(path),
          aqua = es_acuatico(path) && ! file.match(culti_regex);

    if (shp.sum_areas.ids.length > 1 && ! add) {
      shp.reset_hilight();
    }

    let is_sel = false;

    app.map_ui.activate_map(map_num);

    if (recs.ID != null && typeof recs.ID === 'number') {
      const {ID} = recs;

      if (recs.HECTAREAS != null && typeof recs.HECTAREAS === 'number') {
        const HAS = recs.HECTAREAS;

        if (add) {
          is_sel = _.includes(shp.sum_areas.ids, ID);

          if (is_sel) {
            shp.sum_areas.ids  = shp.sum_areas.ids.filter(id => id !== ID);
            shp.sum_areas.sum -= HAS;

            Shp.turn_off_pol(ov, mav);

            show_dbf = false;
          }
          else {
            shp.sum_areas.sum += HAS;
            shp.sum_areas.ids.push(ID);

            Shp.hilight_pol(ov, mav, aqua, ! add);
          }
        }
        else {
          shp.sum_areas.sum = HAS;
          shp.sum_areas.ids = [ID];

          Shp.hilight_pol(ov, mav, aqua, ! add);
        }
      }
      else {
        Shp.hilight_pol(ov, mav, aqua, ! add);
      }
    }
    else {
      Shp.hilight_pol(ov, mav, aqua, ! add);
    }

    const m_area = (shp.sum_areas.sum > 0.00001 && shp.sum_areas.ids.length > 1 ? shp.sum_areas.sum : 0);

    if (app.medidas != null) {
      app.medidas.set_medidas({m_area});
    }

    if (app.dialog_tools != null && app.dialog_tools.state.which_measure === 'sum') {
      const sum_area = (shp.sum_areas.sum > 0.00001 ? shp.sum_areas.sum : 0);

      app.setState({
        result_measure: {
          kind1  : sum_area > 0 ? 'sum' : 'none',
          result1: sum_area > 0 ? `${gen_snum(app.state.num_decimals)(sum_area)} has` : '',
          kind2  : 'none',
          result2: 'none'
        }
      });
    }

    app.map_ui.setState({
      dbf: (show_dbf ? {recs, path, mav} : null)
    });
  }



  // static click_pol_cf(app: App, ov: Object, mav: boolean, path: string, id: number, recs: T_DbfRow, map_num: number, add: boolean): void {
	// 	let show_dbf_true = true;
	//
	// 	if (app.toolbar !== null) {
	// 		app.toolbar.setState({sel_pol_cf[id]: {path, id}});
	// 	}
	//
	// 	const sh
	// }



  static click_dot(app: App, ov: Object, convs: ?{[string]: string}, path: string, recs: T_DbfRow, map_num: number): void {
    app.map_ui.setState({
      dbf: {recs, path, convs: convs != null ? convs : {}}
    });

    Shp.hilight_dot(ov);

    app.map_ui.activate_map(map_num);
  }



  reset_hilight(): void {
    const opt_pol = {fillOpacity: this.obj.is_mav ? 0.7 : Shp.opa.min, strokeWeight: Shp.weight_unselected};

    this.overlays.forEach((ov) => {
      if (this.obj.shptype === 'Polygon') {
        ov.setOptions(opt_pol);
      }
      else if (this.obj.shptype === 'Point') {
        ov.setIcon(Shp.icon_pair.normal);
      }
    });
  }



  get_bounds(): ?Object {
    if (Map.google != null) {
      const google_maps = Map.google,
            bounds = new google_maps.LatLngBounds(),
            ll_min = new google_maps.LatLng(this.obj.min_bound[0], this.obj.min_bound[1]);

      bounds.extend(ll_min);
      bounds.extend(new google_maps.LatLng(this.obj.max_bound[0], this.obj.max_bound[1]));

      return bounds;
    }
    else {
      return null;
    }
  }



  add_frame(): void {
    if (Map.google != null) {
      const {google} = Map,
            sw       = this.bounds.getSouthWest(),
            ne       = this.bounds.getNorthEast(),
            dlat     = Math.abs(sw.lat() - ne.lat()) * 0.04,
            dlng     = Math.abs(sw.lng() - ne.lng()) * 0.04,
            sw2      = new google.LatLng(sw.lat() - dlat, sw.lng() - dlng),
            ne2      = new google.LatLng(ne.lat() + dlat, ne.lng() + dlng),
            bounds   = new google.LatLngBounds(sw2, ne2);

      this.frame = new google.Rectangle({
        strokeColor: '#FD2CEC',
        strokeOpacity: 1,
        strokeWeight: 1,
        fillOpacity: 0,
        map: this.map.map,
        bounds
      });
    }
  }



  remove_frame(): void {
    if (this.frame != null) {
      this.frame.setMap(null);

      this.frame = null;
    }
  }



  constructor(app: App, map: Map, shape: T_ShpFile) {
    if (Map.google != null && Map.vec2LL != null) {
      const {google, vec2LL} = Map,
            is_culti    = takeFilename(shape.shpfile).slice(0, 11) === 'cultivable_',
            es_aqua     = es_acuatico(shape.shpfile) && (! is_culti),
            es_infraest = es_infra(shape.shpfile) && (! is_culti),
            bounds      = map.center_and_zoom(shape.min_bound, shape.max_bound),
            good_dbf    = _.has(shape, 'dbfRecords') && shape.dbfRecords.length > 0;

      let ovs = [];

      if (! shape.html_body) {
        shape.html_body = '';
      }

      if (shape.shptype === 'Polygon') {
        shape.shapes.forEach((shp: T_Shp, i_shp: number) => {
          const opt = _.clone(Shp.polygonOptions);

          if (shape.is_mav) {
            if (shp.area) {
              const {area} = shp;

              if (good_dbf) {
                shape.dbfRecords[i_shp].HECTAREAS = round2(area / 10000);
                shape.dbfRecords[i_shp].AREA      = round2(area);
              }
            }

            if (good_dbf && typeof shape.dbfRecords[i_shp].DOSIS === 'number' && typeof shape.dbfRecords[i_shp].HECTAREAS === 'number') {
              shape.dbfRecords[i_shp]['Dosis total (kg)'] = round2(shape.dbfRecords[i_shp].DOSIS * shape.dbfRecords[i_shp].HECTAREAS);
            }

            if (good_dbf) {
              shape.dbfRecords[i_shp]['Dosis (kg/ha)'] = shape.dbfRecords[i_shp].DOSIS;

              opt.fillColor = `#${shape.dbfRecords[i_shp].COLOR}`;
            }

            opt.fillOpacity = 0.7;
          }

          const pol = new google.Polygon(
            es_aqua ?
              Shp.polygonOptionsAqua
            :
              (es_infraest ? Shp.polygonOptionsInfra : opt)
          );

          pol.setPaths(_.map(shp.rings, ring => _.map(ring, vec2LL)));   // [[Latlong]]
          pol.setMap(map.map);

          if (good_dbf && _.has(shape.dbfRecords[i_shp], 'AREA')) {
            pol.AREA = shape.dbfRecords[i_shp].AREA;
          }

          pol.HECTAREAS = good_dbf ? shape.dbfRecords[i_shp].HECTAREAS : 0;
          pol.ID = shp.shp_id;
          ovs.push(pol);

          google.event.addListener(pol, 'click', (ev) => {
            if (! good_dbf) {
              return;
            }

            let ctrl = false;

            if (app.dialog_tools != null && app.dialog_tools.state.which_measure === 'sum') {
              ctrl = true;
            }
            else if (ev.Aa && ev.Aa.ctrlKey) {
              ctrl = app.dev_mode;
            }

            Shp.click_pol(
              app, pol, shape.is_mav === 1, shape.shpfile, shp.shp_id, shape.dbfRecords[shp.shp_id], map.props.num, ctrl
            );
          });
        });

        shape.total_has = _.sum(_.map(shape.dbfRecords, drec => drec.AREA)) / 10000;

        this.is_culti = is_culti;
      }
      else if (shape.shptype === 'Point') {
        const is_mf    = takeFilename(shape.shpfile).slice(0, 3) === 'mf_',
              my_convs = is_mf ? app.mf_convs : app.normal_convs;

        shape.convs = {};

        this.is_culti = false;

        _.keys(shape.dbf_column_types).forEach((key) => {
          const conv = _.find(my_convs, conv => conv.name === key);

          if (shape.convs != null) {
            shape.convs[key] = conv != null && conv.conversion != null ? conv.conversion : key;
          }
        });

        ovs = _.map(shape.shapes, (shp) => {
          const position = vec2LL(shp.rings[0][0]),
                dot      = new google.Marker({position, map: map.map, icon: Shp.icon_pair.normal});

          google.event.addListener(dot, 'click', (ev) => {
            Shp.click_dot(app, dot, shape.convs, shape.shpfile, shape.dbfRecords[shp.shp_id], map.props.num);
          });

          return dot;
        });
      }
      else if (shape.shptype === 'Arc') {
        const opt = _.clone(Shp.lineOptions);

        if (es_curvas_nivel(shape.shpfile)) {
          opt.strokeColor = '#7f1634';            // NOTE: eso es un burdeo.
        }

        shape.shapes.forEach((shp: T_Shp, i_shp: number) => {
          const lines = _.map(shp.rings, ring => new google.Data.LineString(_.map(ring, vec2LL))),
                line  = new google.Data.MultiLineString(lines),
                feat  = new google.Data.Feature();

          map.map.data.overrideStyle(feat, es_aqua ? Shp.lineOptionsAqua : opt);

          feat.setGeometry(line);
          feat.setProperty('shp_id', shp.shp_id);

          map.map.data.add(feat);

          // FIXME: AGREGA MANEJO DE EVENTOS.

          ovs.push(feat);
        });
      }

      this.obj       = shape;
      this.overlays  = ovs;
      this.labels    = [];
      this.old       = ++Shp.count;
      this.map       = map;
      this.sum_areas = _.cloneDeep(EMPTY_SUM);
      this.frame     = null;

      if (bounds != null) {
        this.bounds = bounds;
      }

      if (this.is_culti || this.obj.is_mav) {
        localforage.setItem(`${app.state.user.user_name}.last_culti`, this.obj.shpfile);
      }

      map.Shp_List[shape.shpfile] = this;
    }
  }



  reload_icons(): void {
    if (this.obj.shptype === 'Point') {
      this.overlays.forEach((ov) => {
        ov.setIcon(Shp.icon_pair.normal);
      });

      if (Shp.clickedMark)
        Shp.clickedMark.setIcon(Shp.icon_pair.hilight);
    }
  }



  labelize(dbf_hl: ?string): void {
    const shape  = this.obj,
          is_dot = shape.shptype === 'Point',
          coords = shape.shapes.map(shp => (is_dot ? shp.rings[0][0] : shp.Centroids[0]));

    if (dbf_hl != null) {
      const hl     = dbf_hl,
            labels = shape.dbfRecords.map(dbf => dbf[hl]);

      this.un_labelize();

      if (Map.vec2LL != null) {
        const {vec2LL} = Map;

        if (Map.MarkerWithLabel != null) {
          const {MarkerWithLabel} = Map,
              labelStyle = {background: 'white', paddingRight: '3px', paddingLeft: '3px', borderRadius: '3px'},
              snum = gen_snum(window.app.state.num_decimals);

          const fmt = (val) => {
            if (typeof val === 'number' && dbf_hl !== 'ID') {
              return snum(val);
            }
            else {
              return val;
            }
          };

          this.labels = _.zip(coords, labels).map(([xy, v]) => {
            return new MarkerWithLabel({
              position: vec2LL(xy),
              icon: './img/no.png',
              map: this.map.map,                // FIXME: calcula el ancho del label, y córrelo a la izquierda para centrarlo
              labelContent: fmt(v),             // con la opción: labelAnchor: new Map.google.Point(some_width, 0)
              labelStyle,
              clickable: false
            });
          });
        }
      }
    }
    else {
      this.un_labelize();
    }
  }

  labelize_cf(dbf_hl: Array<string>): void {
    const
      shape  = this.obj,
      is_dot = shape.shptype === 'Point',
      coords = shape.shapes.map(shp => (is_dot ? shp.rings[0][0] : shp.Centroids[0]))
    ;

    if (dbf_hl != null) {
      const
        hl_id = dbf_hl[0],
        hl_nm = dbf_hl[1],

        labels_id = shape.dbfRecords.map(dbf => dbf[hl_id]),
        labels_nm = shape.dbfRecords.map(dbf => dbf[hl_nm])
      ;

      this.un_labelize();

      if (Map.vec2LL != null) {
        const {vec2LL} = Map;

        if (Map.MarkerWithLabel != null) {
          const {MarkerWithLabel} = Map,
                labelStyle = {
                  background:   'white',
                  paddingRight: '3px',
                  paddingLeft:  '3px',
                  borderRadius: '3px'
                },
                snum = gen_snum(window.app.state.num_decimals)
          ;

          const fmt = (val) => {
            for (const i in dbf_hl) {
              const hl = dbf_hl[i];
              if (typeof val === 'number' && hl !== 'ID') {
                return snum(val);
              } else {
                return val;
              }
            }
          };

          this.labels = _.zip(coords, labels_id, labels_nm).map(([xy,id,nm]) => {
            return new MarkerWithLabel({
              position: vec2LL(xy),
              icon: './img/no.png',
              map: this.map.map,
              // labelContent: `${fmt(id)} ${fmt(nm)}`,
              labelContent: `${fmt(id)}`,
              labelStyle,
              clickable: false
            });
          });
        }
      }

    } else {
      this.un_labelize();
    }
  }

  un_labelize(): void {
    this.labels.forEach(lb => lb.setMap(null));

    this.labels = [];
  }



  set_options(new_conf: Object): void {
    if (this.obj.shptype === 'Polygon') {
      this.overlays.forEach((ov) => {
        ov.setOptions(new_conf);
      });
    }
  }



  destroy(map: Map): void {
    if (Map.google != null) {
      const {google} = Map,
            is_arc   = this.obj.shptype === 'Arc';

      this.un_labelize();

      _.each(this.overlays, (ov) => {
        google.event.clearInstanceListeners(ov);

        if (ov != null) {
          if (is_arc) {
            map.map.data.remove(ov);      // NOTE: las líneas se remueven de esta forma.
          }
          else {
            ov.setMap(null);
          }
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/Shp.js